import axios from "axios"
import {defineStore} from "pinia"
import {computed, ref} from "vue"

import {useFormDataStore} from "@/stores/FormDataStore"
import {useVoucherStore} from "@/stores/VoucherStore"

export const useSubscriptionsStore = defineStore('SubscriptionsStore', () => {

	const dataStore = useFormDataStore()
	const voucherStore = useVoucherStore()

	const isMetaLoaded = ref(true)

	const meta = ref({})

	const variants = ref({})

	const amount = ref(0)

	const activeSubscriptions = computed(() => {
		return window.init.activeSubscriptions || {}
	})

	const getActiveSubscription = (id) => {
		const activeSubscription = Object.values(activeSubscriptions.value).find(subscription => subscription.subscription_type_id === id)
		return activeSubscription || null
	}

	const all = computed(() => {

		const isGiftUsingShop = window.settings.version === 'CZ' && window.init.funnelUrlKey === 'gift'
		let subscriptions = window.init.subscriptions

		if(isGiftUsingShop) {
			subscriptions = window.init.products.reduce((all, product) => {
				const subscription = subscriptions[product.subscription_type_code]
				if(subscription) {
					return {
						...all,
						[product.subscription_type_code]: {
							...subscription,
							user_label: product.user_label,
							printDates: product.print_dates,
							productImage: product.image_url,
							sorting: product.sorting,
							product
						}
					}
				}
				return all
			}, {})
		}

		return Object.values(subscriptions)
			.sort((s1, s2) => (s1.sorting > s2.sorting) ? 1 : (s1.sorting < s2.sorting) ? -1 : 0)
			.map(subscription => {
				const bundledTypes = Object.values(window.init?.subscriptionTypeProducts?.[subscription.code] || {})
				const subscriptionMeta = Object.entries(meta.value[subscription.id] || {})
				const featuresMeta = subscriptionMeta.find(([key]) => key === 'features')
				const isFeaturedMeta = subscriptionMeta.find(([key]) => key === 'is_featured')
				const isBlackFridayDiscountMeta = subscriptionMeta.find(([key]) => key === 'is_black_friday')
				const priceIntervalMeta = subscriptionMeta.find(([key]) => key === 'show_price_interval')
				const calculateDiscountAgainstMeta = subscriptionMeta.find(([key]) => key === 'calculate_discount_against')

				let overriddenLength = null
				let overriddenPrice = null
				let discountAgainst = null

				if(!isGiftUsingShop) {
					if (calculateDiscountAgainstMeta) {
						const sourceSubscription = window.init.subscriptions[calculateDiscountAgainstMeta[1]]
						if (sourceSubscription) {
							const currentPrice = subscription.price
							let sourceSubscriptionPrice = sourceSubscription.price

							if(sourceSubscription.length === 30) {
								sourceSubscriptionPrice = sourceSubscription.price * 12
							}

							discountAgainst = Math.round((1 - (currentPrice / sourceSubscriptionPrice)) * 100)
						}
					}
				}

				let features = null
				if(featuresMeta) {
					features = featuresMeta[1].split(',')
				}
				let value = {
					...subscription,
					activeSubscription: getActiveSubscription(subscription.id),
					bundle: bundledTypes,
					contentAccess: Object.values(window.init.contentAccess[subscription.code]),
					isFeatured: !! (isFeaturedMeta ? isFeaturedMeta[1] : 0),
					isBlackFriday: !! (isBlackFridayDiscountMeta ? isBlackFridayDiscountMeta[1] : 0),
					info: subscriptionMeta
						.filter(([key]) => key.startsWith(`info_`))
						.sort(([key1], [key2]) => {
							if ( key1 < key2 ){
								return -1
							}
							if ( key1 > key2 ){
								return 1
							}
							return 0
						})
						.reduce((acc, [key, value]) => ({
							...acc,
							[key]: value
						}), []),
					overriddenLength,
					overriddenPrice,
					discountAgainst,
					features
				}

				const originalPriceMeta = subscriptionMeta.find(([key]) => key === 'original_price')
				value.originalPrice = originalPriceMeta ? originalPriceMeta[1] : 0
				value.originalPriceDiscount = value.originalPrice ? Math.round((1 - (value.price / value.originalPrice)) * 100) : 0

				if(!isGiftUsingShop) {

					const variantMeta = subscriptionMeta.find(([key]) => key === 'is_variant_for')
					const variant = variantMeta ? variantMeta[1] : null

					const productImageMeta = subscriptionMeta.find(([key]) => key === 'product_image_url')
					const productImage = productImageMeta ? productImageMeta[1] : null

					if(variants.value[variant] === undefined) {
						variants.value[variant] = []
					}
					if(variants.value[variant].includes(subscription.id) === false) {
						variants.value[variant].push(subscription.id)
					}
					value.variant = variant
					value.productImage = productImage

					if (priceIntervalMeta) {
						let months = Math.round(subscription.length/30)
						switch (priceIntervalMeta[1]) {
							case 'weekly':
								value.overriddenLength = 7
								break
							case 'monthly':
								value.overriddenLength = 30
								value.overriddenPrice = subscription.price / months
								value.overriddenOriginalPrice = value.originalPrice / months
								break
							default:
								value.overriddenLength = 365
								break
						}
					}
				}

				return value
			})
	})

	const first = computed(() => all.value.filter(subscription => !subscription.variant)[0])

	const getSubscriptionByCode = code => all.value.find(subscription => subscription.code === code)

	const fetchMeta = () => {
		axios.get(
			`${window.api.baseUrl}/${window.api.subscriptionsMeta.url}`,
			{
				params: {
					id: all.value.reduce((acc, subscription) => ([
						...acc,
						subscription.id
					]), [])
				}
			}
		).then(response => {
			meta.value = response.data.meta
			isMetaLoaded.value = true
		})
	}

	const finalPrice = computed(() => {
		const voucher = voucherStore.voucher
		const selectedSubscriptionCode = dataStore.selectedSubscriptionWithVariant.code

		if(amount.value > 1) {
			return subscriptionPriceForAmount(selectedSubscriptionCode, voucher)
		}

		return subscriptionPrice(selectedSubscriptionCode, voucher)
	})

	const subscriptionPrice = (code, voucher) => {
		if (voucher) {
			return voucher.discounted_amount
		}

		return getSubscriptionByCode(code).price
	}

	const subscriptionPriceForAmount = (code, voucher) => {
		return subscriptionPrice(code, voucher) * amount.value
	}

	const hasVariants = (subscriptionId) => {
		return !!variants.value[subscriptionId]?.length
	}

	const getVariants = (subscriptionId) => {
		const variantIds = variants.value[subscriptionId]

		return all.value.filter(subscription => variantIds.includes(parseInt(subscription.id)))
	}

	const hasContentAccess = (code, access) => {
		const subscription = getSubscriptionByCode(code)

		if (subscription) {
			return !!subscription.contentAccess.find((item) => item === access)
		}

		return false
	}

	const setAmount = (newAmount) => amount.value = newAmount

	return {
		all,
		first,
		finalPrice,
		isMetaLoaded,
		meta,
		variants,
		amount,
		subscriptionPrice,
		subscriptionPriceForAmount,
		getSubscriptionByCode,
		hasContentAccess,
		fetchMeta,
		hasVariants,
		getVariants,
		setAmount
	}
}, {
	persist: [
		{
			paths: ['meta', 'isMetaLoaded']
		}
	]
})